import { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import useIsMobile from '../../hooks/useIsMobile';
import { toast } from 'react-toastify';
import { logout } from '../../store/slices/authSlice';
import { addToCart } from '../../store/slices/cartSlice';
import {
  getProfileApi, updateProfileApi, changePasswordApi, updateNotificationsApi,
  getAddressesApi, addAddressApi, updateAddressApi, setDefaultAddressApi, deleteAddressApi,
  getWishlistApi, removeWishlistApi, getUserCouponsApi,
} from '../../api/userApi';
import { getOrdersApi, cancelOrderApi } from '../../api/orderApi';

/* ── helpers ────────────────────────────────────────────── */
const IMG = (url) => url
  ? (url.startsWith('http') ? url : `${process.env.REACT_APP_API_URL?.replace('/api','') || 'http://localhost:5000'}${url}`)
  : null;

function loyaltyTier(pts) {
  if (pts >= 2000) return { label: 'Platinum', color: '#4338CA', bg: '#E8E8F0' };
  if (pts >= 1000) return { label: 'Gold',     color: '#C8952A', bg: '#FEF3C7' };
  return                   { label: 'Member',   color: '#6B7280', bg: '#F3F4F6' };
}

const STATUS_COLOR = {
  pending:    { bg:'#FFF8E8', fg:'#D97706' },
  confirmed:  { bg:'#EFF6FF', fg:'#2563EB' },
  processing: { bg:'#FFF8E8', fg:'#D97706' },
  shipped:    { bg:'#EFF6FF', fg:'#2563EB' },
  delivered:  { bg:'#EFFFEF', fg:'#16A34A' },
  cancelled:  { bg:'#FFF0F0', fg:'#DC2626' },
};

function StatusBadge({ status }) {
  const c = STATUS_COLOR[status] || STATUS_COLOR.pending;
  return (
    <span style={{ fontSize:'10px', fontWeight:700, letterSpacing:'.5px', textTransform:'uppercase',
      padding:'3px 10px', background:c.bg, color:c.fg }}>{status}</span>
  );
}

function Toggle({ on, onChange }) {
  return (
    <button onClick={() => onChange(!on)} style={{ ...VS.toggle, background: on ? '#1A7A3C' : '#D1D5DB' }}>
      <div style={{ ...VS.knob, transform: on ? 'translateX(18px)' : 'translateX(2px)' }} />
    </button>
  );
}

const ADDR_BLANK = { label:'Home', full_name:'', phone:'', address_line1:'', address_line2:'', city:'', state:'', postal_code:'', country:'India' };

/* ── main component ─────────────────────────────────────── */
export default function Dashboard() {
  const dispatch  = useDispatch();
  const navigate  = useNavigate();
  const { user }  = useSelector((s) => s.auth);

  const isMobile                = useIsMobile();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [tab, setTab]           = useState('overview');
  const [profile, setProfile]   = useState(null);
  const [orders, setOrders]     = useState([]);
  const [wishlist, setWishlist] = useState([]);
  const [addresses, setAddresses] = useState([]);
  const [coupons, setCoupons]   = useState([]);
  const [loading, setLoading]   = useState(true);

  /* load all data once — allSettled so one failing API doesn't wipe everything */
  const loadAll = useCallback(() => {
    setLoading(true);
    Promise.allSettled([
      getProfileApi(),
      getOrdersApi(),
      getWishlistApi(),
      getAddressesApi(),
      getUserCouponsApi(),
    ]).then(([p, o, w, a, c]) => {
      if (p.status === 'fulfilled') setProfile(p.value.data);
      else toast.error('Could not load profile');
      if (o.status === 'fulfilled') setOrders(o.value.data);
      if (w.status === 'fulfilled') setWishlist(w.value.data);
      if (a.status === 'fulfilled') setAddresses(a.value.data);
      if (c.status === 'fulfilled') setCoupons(c.value.data);
    }).finally(() => setLoading(false));
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  const handleLogout = () => { dispatch(logout()); navigate('/'); };

  const tier = loyaltyTier(profile?.loyalty_points || 0);

  const NAV_GROUPS = [
    { group: 'MY ACCOUNT', items: [
      { key:'overview',  icon:'📊', label:'Overview' },
      { key:'orders',    icon:'📦', label:'My Orders',  badge: orders.filter(o => ['processing','shipped','confirmed'].includes(o.status)).length || null },
      { key:'wishlist',  icon:'❤️',  label:'Wishlist',   badge: wishlist.length || null },
      { key:'addresses', icon:'📍', label:'Addresses' },
    ]},
    { group: 'REWARDS', items: [
      { key:'loyalty',   icon:'🌟', label:'Loyalty & Points' },
      { key:'coupons',   icon:'🎫', label:'My Coupons',      badge: coupons.length || null },
    ]},
    { group: 'SETTINGS', items: [
      { key:'profile',       icon:'👤', label:'Profile Settings' },
      { key:'security',      icon:'🔒', label:'Security' },
      { key:'notifications', icon:'🔔', label:'Notifications' },
    ]},
  ];

  if (loading) return (
    <div className="page-top" style={{ display:'flex', alignItems:'center', justifyContent:'center', minHeight:'80vh' }}>
      <div style={VS.spinner} />
    </div>
  );

  const allItems = NAV_GROUPS.flatMap((g) => g.items);
  const currentLabel = allItems.find((i) => i.key === tab)?.label || 'Dashboard';

  const SidebarContent = (
    <>
      <div style={VS.userCard}>
        <div style={VS.avatar}>{(profile?.name || user?.name || '?')[0].toUpperCase()}</div>
        <p style={VS.uName}>{profile?.name || user?.name}</p>
        <p style={VS.uEmail}>{profile?.email || user?.email}</p>
        <span style={{ ...VS.tierBadge, background:tier.bg, color:tier.color }}>★ {tier.label}</span>
      </div>
      <nav style={{ flex:1, padding:'8px 0' }}>
        {NAV_GROUPS.map((g) => (
          <div key={g.group} style={{ marginBottom:'4px' }}>
            <p style={VS.groupLabel}>{g.group}</p>
            {g.items.map((item) => (
              <button key={item.key}
                style={{ ...VS.navItem, ...(tab===item.key ? VS.navActive : {}) }}
                onClick={() => { setTab(item.key); setMobileNavOpen(false); }}>
                <span style={VS.navIcon}>{item.icon}</span>
                <span style={{ flex:1, textAlign:'left' }}>{item.label}</span>
                {item.badge > 0 && <span style={VS.navBadge}>{item.badge}</span>}
                {tab===item.key && <div style={VS.activeBar}/>}
              </button>
            ))}
          </div>
        ))}
      </nav>
      <div style={{ padding:'16px 20px', borderTop:'1px solid rgba(255,255,255,.08)' }}>
        <button style={VS.signOutBtn} onClick={handleLogout}>⏻  Sign Out</button>
      </div>
    </>
  );

  return (
    <div className="page-top" style={VS.root}>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}} @keyframes fadeIn{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:translateY(0)}}`}</style>

      {/* ── Desktop Sidebar ───────────────────────────────── */}
      {!isMobile && <aside style={VS.sidebar}>{SidebarContent}</aside>}

      {/* ── Mobile top bar + drawer ───────────────────────── */}
      {isMobile && (
        <>
          <div style={{ background:'#111', borderBottom:'2px solid var(--red)', padding:'0 16px', height:'52px', display:'flex', alignItems:'center', justifyContent:'space-between', position:'sticky', top:'calc(var(--ticker-h) + var(--nav-h))', zIndex:200 }}>
            <span style={{ fontFamily:'var(--font-display)', fontSize:'15px', letterSpacing:'2px', color:'#fff' }}>{currentLabel.toUpperCase()}</span>
            <button style={{ background:'none', border:'none', cursor:'pointer', display:'flex', flexDirection:'column', gap:'4px', padding:'4px' }} onClick={() => setMobileNavOpen((o) => !o)}>
              <span style={{ display:'block', width:'20px', height:'2px', background:'#fff', borderRadius:'2px' }} />
              <span style={{ display:'block', width:'20px', height:'2px', background:'#fff', borderRadius:'2px' }} />
              <span style={{ display:'block', width:'20px', height:'2px', background:'#fff', borderRadius:'2px' }} />
            </button>
          </div>
          {mobileNavOpen && (
            <div className="mob-drawer">
              <div className="mob-drawer-bg" onClick={() => setMobileNavOpen(false)} />
              <div className="mob-drawer-panel" style={{ background:'#111' }}>{SidebarContent}</div>
            </div>
          )}
        </>
      )}

      {/* ── Main ─────────────────────────────────────────── */}
      <main style={{ ...VS.main, padding: isMobile ? '20px 16px' : '36px' }}>
        {tab==='overview'      && <OverviewPanel   profile={profile} orders={orders} wishlist={wishlist} coupons={coupons} setTab={setTab} />}
        {tab==='orders'        && <OrdersPanel     orders={orders} setOrders={setOrders} />}
        {tab==='wishlist'      && <WishlistPanel   wishlist={wishlist} setWishlist={setWishlist} dispatch={dispatch} />}
        {tab==='addresses'     && <AddressesPanel  addresses={addresses} setAddresses={setAddresses} profile={profile} />}
        {tab==='loyalty'       && <LoyaltyPanel    profile={profile} tier={tier} />}
        {tab==='coupons'       && <CouponsPanel    coupons={coupons} />}
        {tab==='profile'       && <ProfilePanel    profile={profile} setProfile={setProfile} />}
        {tab==='security'      && <SecurityPanel   />}
        {tab==='notifications' && <NotificationsPanel profile={profile} setProfile={setProfile} />}
      </main>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════
   OVERVIEW
══════════════════════════════════════════════════════════ */
function OverviewPanel({ profile, orders, wishlist, coupons, setTab }) {
  const isMobile = useIsMobile();
  const firstName = (profile?.name || '').split(' ')[0] || 'there';
  const activeCoupons = coupons.filter(c => !c.expires_at || new Date(c.expires_at) > new Date());
  const recent = orders.slice(0, 3);

  return (
    <div style={VS.panel}>
      {/* Greeting */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-end', marginBottom:'24px', flexWrap:'wrap', gap:'12px' }}>
        <div>
          <h1 style={VS.pageTitle}>WELCOME BACK, <span style={{ color:'var(--red)' }}>{firstName.toUpperCase()}</span>!</h1>
          <p style={VS.pageSub}>Here's your account snapshot</p>
        </div>
        <Link to="/products" style={VS.btnRed}>🛒 Shop Now</Link>
      </div>

      {/* Stats */}
      <div style={{ ...VS.statGrid, gridTemplateColumns: isMobile ? 'repeat(2,1fr)' : 'repeat(4,1fr)' }}>
        {[
          { icon:'📦', label:'Total Orders',   value:orders.length,             sub:`${orders.filter(o=>['processing','shipped'].includes(o.status)).length} active`, subColor:'#16A34A' },
          { icon:'🌟', label:'Adda Points',    value:profile?.loyalty_points||0, sub:'+10% on every purchase',           subColor:'#C8952A', valColor:'#C8952A' },
          { icon:'❤️',  label:'Wishlist Items', value:wishlist.length,            sub:'Saved for later',                  subColor:'#6B7280' },
          { icon:'🎫', label:'Active Coupons', value:activeCoupons.length,       sub:activeCoupons.length?'Ready to use':'No coupons yet', subColor:'#16A34A' },
        ].map((s) => (
          <div key={s.label} style={VS.statCard}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start' }}>
              <div>
                <p style={VS.statLabel}>{s.label}</p>
                <p style={{ ...VS.statVal, color: s.valColor||'#111' }}>{s.value.toLocaleString('en-IN')}</p>
                <p style={{ fontSize:'11px', color:s.subColor, margin:'4px 0 0', fontWeight:600 }}>{s.sub}</p>
              </div>
              <span style={{ fontSize:'26px' }}>{s.icon}</span>
            </div>
          </div>
        ))}
      </div>

      {/* Recent Orders */}
      <div style={VS.card}>
        <div style={VS.cardHead}>
          <span style={VS.cardTitle}>RECENT ORDERS</span>
          <button style={VS.linkBtn} onClick={() => setTab('orders')}>View All →</button>
        </div>
        {recent.length === 0
          ? <p style={VS.emptyMsg}>No orders yet. <Link to="/products" style={{ color:'var(--red)' }}>Start shopping!</Link></p>
          : (
            <table style={VS.table}>
              <thead><tr>{['Order ID','Date','Total','Status','Action'].map(h=><th key={h} style={VS.th}>{h}</th>)}</tr></thead>
              <tbody>
                {recent.map(o => (
                  <tr key={o.id} style={VS.tr}>
                    <td style={VS.td}><strong style={{ fontFamily:'var(--font-display)', letterSpacing:'1px', fontSize:'13px' }}>#{o.id.slice(0,8).toUpperCase()}</strong></td>
                    <td style={VS.td}>{new Date(o.placed_at).toLocaleDateString('en-IN',{day:'numeric',month:'short',year:'numeric'})}</td>
                    <td style={VS.td}><strong>₹{parseFloat(o.total_amount).toLocaleString('en-IN')}</strong></td>
                    <td style={VS.td}><StatusBadge status={o.status} /></td>
                    <td style={VS.td}>
                      <button style={VS.actionBtn} onClick={() => toast.info(`Order #${o.id.slice(0,8).toUpperCase()} · Status: ${o.status}`)}>Track</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )
        }
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════
   ORDERS
══════════════════════════════════════════════════════════ */
function OrdersPanel({ orders, setOrders }) {
  const [filter, setFilter] = useState('all');

  const shown = filter==='all'       ? orders
    : filter==='active'    ? orders.filter(o=>['pending','confirmed','processing','shipped'].includes(o.status))
    : filter==='delivered' ? orders.filter(o=>o.status==='delivered')
    : orders.filter(o=>o.status==='cancelled');

  const handleCancel = async (id) => {
    if (!window.confirm('Cancel this order?')) return;
    try {
      await cancelOrderApi(id);
      setOrders(prev => prev.map(o => o.id===id ? {...o, status:'cancelled'} : o));
      toast.success('Order cancelled');
    } catch(e) { toast.error(e.response?.data?.message || 'Cannot cancel'); }
  };

  const TABS = [
    { key:'all',       label:'All',       count: orders.length },
    { key:'active',    label:'Active',    count: orders.filter(o=>['pending','confirmed','processing','shipped'].includes(o.status)).length },
    { key:'delivered', label:'Delivered', count: orders.filter(o=>o.status==='delivered').length },
    { key:'cancelled', label:'Cancelled', count: orders.filter(o=>o.status==='cancelled').length },
  ];

  return (
    <div style={VS.panel}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-end', marginBottom:'20px' }}>
        <div>
          <h1 style={VS.pageTitle}>MY <span style={{ color:'var(--red)' }}>ORDERS</span></h1>
          <p style={VS.pageSub}>{orders.length} orders total</p>
        </div>
      </div>

      {/* Filter tabs */}
      <div style={{ display:'flex', gap:'0', borderBottom:'2px solid #E5E5E5', marginBottom:'20px' }}>
        {TABS.map(t => (
          <button key={t.key}
            style={{ ...VS.filterTab, ...(filter===t.key ? VS.filterTabActive : {}) }}
            onClick={() => setFilter(t.key)}>
            {t.label} <span style={{ fontSize:'11px', opacity:.7 }}>({t.count})</span>
          </button>
        ))}
      </div>

      {shown.length === 0
        ? <div style={VS.emptyBox}><p style={{ fontSize:'40px' }}>📦</p><p>No {filter==='all'?'':filter} orders</p></div>
        : (
          <div style={VS.card}>
            <table style={VS.table}>
              <thead><tr>{['Order ID','Date','Amount','Status','Actions'].map(h=><th key={h} style={VS.th}>{h}</th>)}</tr></thead>
              <tbody>
                {shown.map(o => (
                  <tr key={o.id} style={VS.tr}>
                    <td style={VS.td}><strong style={{ fontFamily:'var(--font-display)', letterSpacing:'1px' }}>#{o.id.slice(0,8).toUpperCase()}</strong></td>
                    <td style={VS.td}>{new Date(o.placed_at).toLocaleDateString('en-IN',{day:'numeric',month:'short',year:'numeric'})}</td>
                    <td style={VS.td}><strong>₹{parseFloat(o.total_amount).toLocaleString('en-IN')}</strong></td>
                    <td style={VS.td}><StatusBadge status={o.status} /></td>
                    <td style={VS.td}>
                      <div style={{ display:'flex', gap:'6px' }}>
                        <button style={VS.actionBtn} onClick={() => toast.info(`Tracking: ${o.tracking_number||'Not available yet'}`)}>Track</button>
                        <button style={VS.actionBtn} onClick={() => toast.info('Invoice download — coming soon')}>Invoice</button>
                        {['pending','confirmed','processing'].includes(o.status) && (
                          <button style={{ ...VS.actionBtn, color:'var(--red)', borderColor:'var(--red)' }} onClick={() => handleCancel(o.id)}>Cancel</button>
                        )}
                        {o.status==='delivered' && (
                          <button style={VS.actionBtn} onClick={() => toast.info('Return request noted')}>Return</button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      }
    </div>
  );
}

/* ══════════════════════════════════════════════════════════
   WISHLIST
══════════════════════════════════════════════════════════ */
function WishlistPanel({ wishlist, setWishlist, dispatch }) {
  const isMobile = useIsMobile();
  const handleRemove = async (id) => {
    try {
      await removeWishlistApi(id);
      setWishlist(prev => prev.filter(w => w.id !== id));
      toast.success('Removed from wishlist');
    } catch { toast.error('Failed to remove'); }
  };

  const handleMoveToCart = (item) => {
    dispatch(addToCart({ product_id: item.product_id, quantity: 1, variant_id: null }));
    toast.success(`${item.name} added to cart!`);
  };

  return (
    <div style={VS.panel}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-end', marginBottom:'24px' }}>
        <div>
          <h1 style={VS.pageTitle}>MY <span style={{ color:'var(--red)' }}>WISHLIST</span></h1>
          <p style={VS.pageSub}>{wishlist.length} items saved</p>
        </div>
        <Link to="/products" style={VS.btnRed}>Browse More</Link>
      </div>

      {wishlist.length === 0
        ? <div style={VS.emptyBox}><p style={{ fontSize:'40px' }}>❤️</p><p>Your wishlist is empty</p><Link to="/products" style={{ color:'var(--red)', fontWeight:700 }}>Start Shopping →</Link></div>
        : (
          <div style={{ ...VS.wishGrid, gridTemplateColumns: isMobile ? 'repeat(2,1fr)' : 'repeat(3,1fr)' }}>
            {wishlist.map(w => (
              <div key={w.id} style={VS.wishCard}>
                <button style={VS.wishDel} onClick={() => handleRemove(w.id)} title="Remove">×</button>
                <div style={VS.wishImg}>
                  {IMG(w.image)
                    ? <img src={IMG(w.image)} alt={w.name} style={{ width:'100%', height:'100%', objectFit:'cover' }} />
                    : <span style={{ fontSize:'48px' }}>👕</span>
                  }
                </div>
                <div style={{ padding:'12px 14px 14px' }}>
                  <p style={{ fontWeight:700, fontSize:'13px', marginBottom:'4px', color:'#111' }}>{w.name}</p>
                  <p style={{ color:'var(--red)', fontWeight:700, fontSize:'14px', marginBottom:'12px' }}>₹{parseFloat(w.price).toLocaleString('en-IN')}</p>
                  <button style={VS.wishCartBtn} onClick={() => handleMoveToCart(w)}>Move to Cart</button>
                </div>
              </div>
            ))}
          </div>
        )
      }
    </div>
  );
}

/* ══════════════════════════════════════════════════════════
   ADDRESSES
══════════════════════════════════════════════════════════ */
function AddressesPanel({ addresses, setAddresses, profile }) {
  const isMobile = useIsMobile();
  const [modal, setModal]   = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm]     = useState(ADDR_BLANK);
  const [saving, setSaving] = useState(false);

  const openAdd  = () => { setEditing(null); setForm({ ...ADDR_BLANK, full_name: profile?.name||'', phone: profile?.phone||'' }); setModal(true); };
  const openEdit = (a) => { setEditing(a.id); setForm({ label:a.label||'Home', full_name:a.full_name, phone:a.phone, address_line1:a.address_line1, address_line2:a.address_line2||'', city:a.city, state:a.state, postal_code:a.postal_code, country:a.country||'India' }); setModal(true); };

  const handleSave = async () => {
    if (!form.full_name||!form.phone||!form.address_line1||!form.city||!form.state||!form.postal_code)
      return toast.error('Please fill all required fields');
    setSaving(true);
    try {
      if (editing) {
        const r = await updateAddressApi(editing, form);
        setAddresses(prev => prev.map(a => a.id===editing ? r.data : a));
        toast.success('Address updated');
      } else {
        const r = await addAddressApi({ ...form, is_default: addresses.length===0 });
        setAddresses(prev => [...prev, r.data]);
        toast.success('Address added');
      }
      setModal(false);
    } catch(e) { toast.error(e.response?.data?.message||'Save failed'); }
    finally { setSaving(false); }
  };

  const handleSetDefault = async (id) => {
    try {
      await setDefaultAddressApi(id);
      setAddresses(prev => prev.map(a => ({ ...a, is_default: a.id===id })));
      toast.success('Default address updated');
    } catch { toast.error('Failed to update'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this address?')) return;
    try {
      await deleteAddressApi(id);
      setAddresses(prev => prev.filter(a => a.id!==id));
      toast.success('Address deleted');
    } catch { toast.error('Delete failed'); }
  };

  return (
    <div style={VS.panel}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-end', marginBottom:'24px' }}>
        <div>
          <h1 style={VS.pageTitle}>MY <span style={{ color:'var(--red)' }}>ADDRESSES</span></h1>
          <p style={VS.pageSub}>Saved delivery addresses</p>
        </div>
        <button style={VS.btnRed} onClick={openAdd}>+ Add New</button>
      </div>

      <div style={{ ...VS.addrGrid, gridTemplateColumns: isMobile ? '1fr' : 'repeat(2,1fr)' }}>
        {addresses.map(a => (
          <div key={a.id} style={{ ...VS.addrCard, borderColor: a.is_default ? '#1A7A3C' : '#E5E5E5' }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:'10px' }}>
              <div style={{ display:'flex', alignItems:'center', gap:'8px' }}>
                <span style={{ fontWeight:700, fontSize:'14px', color:'#111' }}>{a.label || 'Home'}</span>
                {a.is_default && <span style={{ fontSize:'10px', fontWeight:700, background:'#DCFCE7', color:'#16A34A', padding:'2px 8px', borderRadius:'99px' }}>DEFAULT</span>}
              </div>
            </div>
            <p style={{ fontSize:'13px', color:'#374151', lineHeight:1.6, marginBottom:'6px' }}>
              {a.full_name}<br/>
              {a.address_line1}{a.address_line2 ? `, ${a.address_line2}` : ''}<br/>
              {a.city} — {a.postal_code}<br/>
              {a.state}, {a.country}
            </p>
            <p style={{ fontSize:'12px', color:'#6B7280', marginBottom:'14px' }}>📞 {a.phone}</p>
            <div style={{ display:'flex', gap:'8px' }}>
              <button style={VS.addrBtn} onClick={() => openEdit(a)}>Edit</button>
              {!a.is_default && <button style={VS.addrBtn} onClick={() => handleSetDefault(a.id)}>Set Default</button>}
              {!a.is_default && <button style={{ ...VS.addrBtn, color:'#DC2626', borderColor:'#DC2626' }} onClick={() => handleDelete(a.id)}>Delete</button>}
            </div>
          </div>
        ))}

        {/* Add new card */}
        <button style={VS.addrAddCard} onClick={openAdd}>
          <span style={{ fontSize:'28px', marginBottom:'8px', display:'block' }}>+</span>
          <span style={{ fontSize:'13px', fontWeight:700, color:'#6B7280' }}>Add New Address</span>
        </button>
      </div>

      {/* Modal */}
      {modal && (
        <div style={VS.overlay} onClick={() => setModal(false)}>
          <div style={VS.modal} onClick={e => e.stopPropagation()}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'20px' }}>
              <span style={VS.modalTitle}>{editing?'EDIT ADDRESS':'NEW ADDRESS'}</span>
              <button style={VS.closeBtn} onClick={() => setModal(false)}>✕</button>
            </div>
            <div style={VS.formGrid}>
              <div style={{ ...VS.field, gridColumn:'1/-1' }}>
                <label style={VS.label}>Label (Home / Office / Other)</label>
                <input style={VS.input} value={form.label} onChange={e => setForm(f=>({...f,label:e.target.value}))} placeholder="Home" />
              </div>
              <div style={VS.field}>
                <label style={VS.label}>Full Name *</label>
                <input style={VS.input} value={form.full_name} onChange={e => setForm(f=>({...f,full_name:e.target.value}))} />
              </div>
              <div style={VS.field}>
                <label style={VS.label}>Phone *</label>
                <input style={VS.input} value={form.phone} onChange={e => setForm(f=>({...f,phone:e.target.value}))} />
              </div>
              <div style={{ ...VS.field, gridColumn:'1/-1' }}>
                <label style={VS.label}>Address Line 1 *</label>
                <input style={VS.input} value={form.address_line1} onChange={e => setForm(f=>({...f,address_line1:e.target.value}))} placeholder="Street, Area" />
              </div>
              <div style={{ ...VS.field, gridColumn:'1/-1' }}>
                <label style={VS.label}>Address Line 2</label>
                <input style={VS.input} value={form.address_line2} onChange={e => setForm(f=>({...f,address_line2:e.target.value}))} placeholder="Landmark (optional)" />
              </div>
              <div style={VS.field}>
                <label style={VS.label}>City *</label>
                <input style={VS.input} value={form.city} onChange={e => setForm(f=>({...f,city:e.target.value}))} />
              </div>
              <div style={VS.field}>
                <label style={VS.label}>PIN Code *</label>
                <input style={VS.input} value={form.postal_code} onChange={e => setForm(f=>({...f,postal_code:e.target.value}))} />
              </div>
              <div style={VS.field}>
                <label style={VS.label}>State *</label>
                <input style={VS.input} value={form.state} onChange={e => setForm(f=>({...f,state:e.target.value}))} />
              </div>
              <div style={VS.field}>
                <label style={VS.label}>Country</label>
                <input style={VS.input} value={form.country} onChange={e => setForm(f=>({...f,country:e.target.value}))} />
              </div>
            </div>
            <div style={{ display:'flex', gap:'10px', justifyContent:'flex-end' }}>
              <button style={VS.btnOutline} onClick={() => setModal(false)}>Cancel</button>
              <button style={VS.btnRed} onClick={handleSave} disabled={saving}>{saving?'Saving...':editing?'Update Address':'Save Address'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════
   LOYALTY
══════════════════════════════════════════════════════════ */
function LoyaltyPanel({ profile, tier }) {
  const isMobile = useIsMobile();
  const pts  = profile?.loyalty_points || 0;
  const next = pts < 1000 ? { label:'Gold', target:1000 } : pts < 2000 ? { label:'Platinum', target:2000 } : null;
  const pct  = next ? Math.min((pts / next.target) * 100, 100) : 100;

  const PERKS = [
    { icon:'🎁', title:'Early Access',     desc:'First look at new drops' },
    { icon:'🎫', title:'10% Cashback',     desc:'On every purchase as points' },
    { icon:'📸', title:'Free Returns',     desc:'Hassle-free 14-day returns' },
    { icon:'🍰', title:'Birthday Bonus',   desc:'200 extra points on your birthday' },
    { icon:'📞', title:'Priority Support', desc:'Dedicated customer line' },
    { icon:'👕', title:'Exclusive Merch',  desc:"Members-only limited pieces" },
  ];

  return (
    <div style={VS.panel}>
      <h1 style={{ ...VS.pageTitle, marginBottom:'24px' }}>LOYALTY & <span style={{ color:'var(--red)' }}>POINTS</span></h1>

      {/* Loyalty Card */}
      <div style={VS.loyaltyCard}>
        <div style={{ position:'absolute', right:'-10px', top:'-20px', fontFamily:'var(--font-display)', fontSize:'120px', color:'rgba(255,255,255,.04)', userSelect:'none', letterSpacing:'-4px' }}>ADDA</div>
        <div style={{ position:'relative' }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:'12px' }}>
            <div>
              <p style={{ fontFamily:'var(--font-display)', fontSize:'22px', letterSpacing:'2px', color:'#fff', margin:0 }}>{profile?.name}</p>
              <p style={{ fontSize:'10px', fontWeight:700, letterSpacing:'2px', color:'#C8952A', textTransform:'uppercase', margin:'4px 0 0' }}>★ {tier.label}</p>
            </div>
            <p style={{ fontFamily:'var(--font-display)', fontSize:'52px', color:'#C8952A', lineHeight:1, margin:0 }}>{pts.toLocaleString('en-IN')}</p>
          </div>
          <p style={{ fontSize:'12px', color:'rgba(255,255,255,.5)', marginBottom:'16px' }}>= ₹{(pts * 0.1).toFixed(0)} redeemable value · 1 pt = ₹0.10</p>
          {/* Progress */}
          <div style={{ height:'6px', background:'rgba(255,255,255,.1)', borderRadius:'99px', marginBottom:'8px' }}>
            <div style={{ height:'100%', width:`${pct}%`, background:'linear-gradient(90deg,#C8952A,#F0C04A)', borderRadius:'99px', transition:'width .6s' }} />
          </div>
          <div style={{ display:'flex', justifyContent:'space-between', fontSize:'11px', color:'rgba(255,255,255,.4)' }}>
            <span>Gold (1,000)</span>
            <span>Platinum (2,000)</span>
          </div>
          {next && <p style={{ fontSize:'12px', color:'rgba(255,255,255,.5)', marginTop:'8px' }}>
            Current: <strong style={{ color:'#C8952A' }}>{pts} pts</strong> · Need <strong style={{ color:'#fff' }}>{next.target - pts}</strong> more for {next.label}
          </p>}
          {!next && <p style={{ fontSize:'12px', color:'#C8952A', marginTop:'8px', fontWeight:700 }}>✓ Maximum tier reached!</p>}
        </div>
      </div>

      {/* Perks */}
      <h2 style={{ fontFamily:'var(--font-display)', fontSize:'16px', letterSpacing:'2px', color:'#374151', margin:'28px 0 16px' }}>MEMBER PERKS</h2>
      <div style={{ ...VS.perksGrid, gridTemplateColumns: isMobile ? '1fr' : 'repeat(3,1fr)' }}>
        {PERKS.map(p => (
          <div key={p.title} style={VS.perkCard}>
            <span style={{ fontSize:'24px', marginBottom:'8px', display:'block' }}>{p.icon}</span>
            <p style={{ fontWeight:700, fontSize:'13px', color:'#111', marginBottom:'4px' }}>{p.title}</p>
            <p style={{ fontSize:'12px', color:'#6B7280', lineHeight:1.5 }}>{p.desc}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════
   COUPONS
══════════════════════════════════════════════════════════ */
function CouponsPanel({ coupons }) {
  const COUPON_COLORS = [
    { border:'#C8952A', bg:'#FFFCF0', labelFg:'#C8952A', btnBg:'#C8952A', btnFg:'#111' },
    { border:'#1A7A3C', bg:'#F0FFF4', labelFg:'#1A7A3C', btnBg:'#1A7A3C', btnFg:'#fff' },
    { border:'#E8191E', bg:'#FFF5F5', labelFg:'#E8191E', btnBg:'#E8191E', btnFg:'#fff' },
    { border:'#2563EB', bg:'#EFF6FF', labelFg:'#2563EB', btnBg:'#2563EB', btnFg:'#fff' },
  ];

  const handleCopy = (code) => {
    navigator.clipboard.writeText(code).then(() => toast.success(`Code "${code}" copied!`));
  };

  const formatDiscount = (c) => {
    if (c.type === 'percentage') return `${c.discount_value}% OFF`;
    if (c.type === 'fixed')      return `₹${c.discount_value} OFF`;
    return 'FREE SHIPPING';
  };

  return (
    <div style={VS.panel}>
      <div style={{ marginBottom:'24px' }}>
        <h1 style={VS.pageTitle}>MY <span style={{ color:'var(--red)' }}>COUPONS</span></h1>
        <p style={VS.pageSub}>{coupons.length} available coupons</p>
      </div>

      {coupons.length === 0
        ? <div style={VS.emptyBox}><p style={{ fontSize:'40px' }}>🎫</p><p>No coupons available right now</p><p style={{ fontSize:'12px', color:'#9CA3AF' }}>Check back soon for new deals!</p></div>
        : (
          <div style={VS.couponGrid}>
            {coupons.map((c, i) => {
              const clr  = COUPON_COLORS[i % COUPON_COLORS.length];
              const exp  = c.expires_at ? new Date(c.expires_at) : null;
              const days = exp ? Math.ceil((exp - new Date()) / 86400000) : null;
              return (
                <div key={c.id} style={{ ...VS.couponCard, borderColor:clr.border, background:clr.bg }}>
                  <p style={{ fontSize:'10px', fontWeight:700, letterSpacing:'1.5px', textTransform:'uppercase', color:clr.labelFg, marginBottom:'6px' }}>{formatDiscount(c)}</p>
                  <p style={{ fontFamily:'var(--font-display)', fontSize:'28px', letterSpacing:'3px', color:'#111', margin:'0 0 6px' }}>{c.code}</p>
                  <p style={{ fontSize:'12px', color:'#6B7280', marginBottom:'4px' }}>
                    {c.min_order_amount > 0 ? `Min. purchase ₹${c.min_order_amount}` : 'No minimum purchase'}
                  </p>
                  {exp && (
                    <p style={{ fontSize:'11px', fontWeight:700, color: days <= 3 ? '#DC2626' : '#6B7280', marginBottom:'12px' }}>
                      {days <= 0 ? '⚠ Expired' : `Expires in ${days} day${days!==1?'s':''}`}
                    </p>
                  )}
                  <button style={{ ...VS.couponBtn, background:clr.btnBg, color:clr.btnFg }} onClick={() => handleCopy(c.code)}>
                    Copy Code
                  </button>
                </div>
              );
            })}
          </div>
        )
      }
    </div>
  );
}

/* ══════════════════════════════════════════════════════════
   PROFILE SETTINGS
══════════════════════════════════════════════════════════ */
function ProfilePanel({ profile, setProfile }) {
  const [form, setForm]   = useState({ name:'', phone:'', dob:'', gender:'', bio:'' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (profile) setForm({ name:profile.name||'', phone:profile.phone||'', dob:profile.dob?.split('T')[0]||'', gender:profile.gender||'', bio:profile.bio||'' });
  }, [profile]);

  const handleSave = async () => {
    if (!form.name.trim()) return toast.error('Name is required');
    setSaving(true);
    try {
      const r = await updateProfileApi(form);
      setProfile(prev => ({ ...prev, ...r.data }));
      toast.success('Profile updated successfully');
    } catch(e) { toast.error(e.response?.data?.message||'Update failed'); }
    finally { setSaving(false); }
  };

  const f = (key) => ({ value: form[key], onChange: e => setForm(p => ({...p, [key]: e.target.value})) });

  return (
    <div style={VS.panel}>
      <div style={{ marginBottom:'24px' }}>
        <h1 style={VS.pageTitle}>PROFILE <span style={{ color:'var(--red)' }}>SETTINGS</span></h1>
        <p style={VS.pageSub}>Manage your personal information</p>
      </div>

      <div style={VS.settingsCard}>
        <p style={VS.sectionTitle}>PERSONAL INFORMATION</p>
        <div style={VS.formGrid}>
          <div style={VS.field}>
            <label style={VS.label}>Full Name *</label>
            <input style={VS.input} {...f('name')} placeholder="Your full name" />
          </div>
          <div style={VS.field}>
            <label style={VS.label}>Phone Number</label>
            <input style={VS.input} {...f('phone')} placeholder="+91 98765 43210" />
          </div>
          <div style={VS.field}>
            <label style={VS.label}>Date of Birth</label>
            <input type="date" style={VS.input} {...f('dob')} />
          </div>
          <div style={VS.field}>
            <label style={VS.label}>Gender</label>
            <select style={VS.input} {...f('gender')}>
              <option value="">Prefer not to say</option>
              <option value="male">Male</option>
              <option value="female">Female</option>
              <option value="non-binary">Non-binary</option>
            </select>
          </div>
          <div style={{ ...VS.field, gridColumn:'1/-1' }}>
            <label style={VS.label}>About Me (optional)</label>
            <textarea style={{ ...VS.input, resize:'vertical', minHeight:'80px' }} {...f('bio')} placeholder="Kolkata at heart, Bhadralok by identity..." />
          </div>
        </div>
        <div style={{ display:'flex', gap:'10px' }}>
          <button style={VS.btnRed} onClick={handleSave} disabled={saving}>{saving?'Saving...':'Save Changes'}</button>
          <button style={VS.btnOutline} onClick={() => profile && setForm({ name:profile.name||'', phone:profile.phone||'', dob:profile.dob?.split('T')[0]||'', gender:profile.gender||'', bio:profile.bio||'' })}>Discard</button>
        </div>
      </div>
    </div>
  );
}

/* ── EyeBtn must be outside SecurityPanel so it isn't re-mounted on each keystroke ── */
function EyeBtn({ visible, onToggle }) {
  return (
    <button type="button" onClick={onToggle}
      style={{ position:'absolute', right:'12px', top:'50%', transform:'translateY(-50%)', background:'none', border:'none', cursor:'pointer', fontSize:'14px', color:'#9CA3AF' }}>
      {visible ? '🙈' : '👁'}
    </button>
  );
}

/* ══════════════════════════════════════════════════════════
   SECURITY
══════════════════════════════════════════════════════════ */
function SecurityPanel() {
  const [form, setForm]     = useState({ current_password:'', new_password:'', confirm:'' });
  const [saving, setSaving] = useState(false);
  const [show, setShow]     = useState({ cur:false, nw:false, cf:false });

  const handleChange = async () => {
    if (form.new_password !== form.confirm) return toast.error('New passwords do not match');
    if (form.new_password.length < 8)       return toast.error('Password must be at least 8 characters');
    setSaving(true);
    try {
      await changePasswordApi({ current_password: form.current_password, new_password: form.new_password });
      toast.success('Password updated successfully');
      setForm({ current_password:'', new_password:'', confirm:'' });
    } catch(e) { toast.error(e.response?.data?.message||'Update failed'); }
    finally { setSaving(false); }
  };

  return (
    <div style={VS.panel}>
      <div style={{ marginBottom:'24px' }}>
        <h1 style={VS.pageTitle}>SECURITY <span style={{ color:'var(--red)' }}>SETTINGS</span></h1>
        <p style={VS.pageSub}>Keep your account safe</p>
      </div>

      <div style={VS.settingsCard}>
        <p style={VS.sectionTitle}>CHANGE PASSWORD</p>
        <div style={{ display:'flex', flexDirection:'column', gap:'14px', maxWidth:'440px' }}>
          {[['current_password','cur','Current Password','Enter current password'],['new_password','nw','New Password','Min 8 characters'],['confirm','cf','Confirm New Password','Repeat new password']].map(([key, sk, lbl, ph]) => (
            <div key={key} style={VS.field}>
              <label style={VS.label}>{lbl}</label>
              <div style={{ position:'relative' }}>
                <input type={show[sk]?'text':'password'} style={{ ...VS.input, paddingRight:'40px' }}
                  value={form[key]} onChange={e => setForm(p=>({...p,[key]:e.target.value}))} placeholder={ph} />
                <EyeBtn visible={show[sk]} onToggle={() => setShow(p=>({...p,[sk]:!p[sk]}))} />
              </div>
            </div>
          ))}
          <button style={{ ...VS.btnRed, width:'fit-content' }} onClick={handleChange} disabled={saving}>{saving?'Updating...':'Update Password'}</button>
        </div>
      </div>

      <div style={{ ...VS.settingsCard, marginTop:'16px' }}>
        <p style={VS.sectionTitle}>TWO-FACTOR AUTHENTICATION</p>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
          <div>
            <p style={{ fontWeight:700, color:'#111', fontSize:'14px', marginBottom:'4px' }}>SMS Authentication</p>
            <p style={{ fontSize:'13px', color:'#6B7280' }}>Add an extra layer of security to your account</p>
          </div>
          <button style={VS.btnOutline} onClick={() => toast.info('2FA coming soon')}>Enable 2FA</button>
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════
   NOTIFICATIONS
══════════════════════════════════════════════════════════ */
const NOTIF_ITEMS = [
  { key:'order_confirmations', label:'Order Confirmations', desc:'Email when an order is placed or confirmed' },
  { key:'shipping_updates',    label:'Shipping Updates',    desc:'SMS and email for shipping and delivery' },
  { key:'offers',              label:'Offers & Promotions', desc:'Exclusive deals, new drops, and seasonal sales' },
  { key:'wishlist_alerts',     label:'Wishlist Alerts',     desc:'Price drops on items in your wishlist' },
  { key:'loyalty_points',      label:'Loyalty Points',      desc:'Earn and redeem Adda Points updates' },
  { key:'product_reviews',     label:'Product Reviews',     desc:'Reminders to review delivered products' },
];

const DEFAULT_NOTIF_PREFS = { order_confirmations:true, shipping_updates:true, offers:true, wishlist_alerts:true, loyalty_points:false, product_reviews:false };

function NotificationsPanel({ profile, setProfile }) {
  const [prefs, setPrefs] = useState(DEFAULT_NOTIF_PREFS);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (profile?.notification_prefs) setPrefs({ ...DEFAULT_NOTIF_PREFS, ...profile.notification_prefs });
  }, [profile]);

  const toggle = (key) => setPrefs(p => ({ ...p, [key]: !p[key] }));

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateNotificationsApi(prefs);
      setProfile(prev => ({ ...prev, notification_prefs: prefs }));
      toast.success('Notification preferences saved');
    } catch { toast.error('Save failed'); }
    finally { setSaving(false); }
  };

  return (
    <div style={VS.panel}>
      <div style={{ marginBottom:'24px' }}>
        <h1 style={VS.pageTitle}>NOTIFICATION <span style={{ color:'var(--red)' }}>SETTINGS</span></h1>
        <p style={VS.pageSub}>Choose what you hear from us</p>
      </div>

      <div style={VS.settingsCard}>
        {NOTIF_ITEMS.map((item, i) => (
          <div key={item.key} style={{ ...VS.notifRow, borderBottom: i<NOTIF_ITEMS.length-1 ? '1px solid #F3F4F6' : 'none' }}>
            <div>
              <p style={{ fontWeight:700, color:'#111', fontSize:'14px', margin:'0 0 3px' }}>{item.label}</p>
              <p style={{ fontSize:'12px', color:'#6B7280', margin:0 }}>{item.desc}</p>
            </div>
            <Toggle on={!!prefs[item.key]} onChange={() => toggle(item.key)} />
          </div>
        ))}
        <div style={{ marginTop:'20px' }}>
          <button style={VS.btnRed} onClick={handleSave} disabled={saving}>{saving?'Saving...':'Save Preferences'}</button>
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════
   STYLES
══════════════════════════════════════════════════════════ */
const VS = {
  root:        { display:'flex', minHeight:'100vh', background:'#F7F7F2', fontFamily:'var(--font-body)' },

  /* Sidebar */
  sidebar:     { width:'260px', minWidth:'260px', background:'#111', display:'flex', flexDirection:'column', position:'sticky', top:'calc(var(--ticker-h) + var(--nav-h))', height:'calc(100vh - var(--ticker-h) - var(--nav-h))', overflowY:'auto', flexShrink:0 },
  userCard:    { padding:'28px 20px 20px', borderBottom:'1px solid rgba(255,255,255,.08)', textAlign:'center' },
  avatar:      { width:'64px', height:'64px', background:'var(--red)', border:'3px solid #C8952A', borderRadius:'50%', display:'flex', alignItems:'center', justifyContent:'center', fontFamily:'var(--font-display)', fontSize:'26px', color:'#fff', margin:'0 auto 12px' },
  uName:       { fontFamily:'var(--font-display)', fontSize:'16px', letterSpacing:'1px', color:'#fff', margin:'0 0 3px' },
  uEmail:      { fontSize:'12px', color:'rgba(255,255,255,.4)', margin:'0 0 10px' },
  tierBadge:   { display:'inline-block', fontSize:'10px', fontWeight:700, letterSpacing:'1px', textTransform:'uppercase', padding:'3px 10px', borderRadius:'99px' },
  groupLabel:  { fontSize:'9px', fontWeight:700, letterSpacing:'2.5px', color:'rgba(255,255,255,.25)', textTransform:'uppercase', padding:'12px 20px 4px', margin:0 },
  navItem:     { width:'100%', display:'flex', alignItems:'center', gap:'10px', padding:'11px 20px', background:'none', border:'none', cursor:'pointer', fontFamily:'var(--font-body)', fontSize:'13px', color:'rgba(255,255,255,.6)', position:'relative', transition:'background .15s', textAlign:'left' },
  navActive:   { background:'rgba(255,255,255,.07)', color:'#fff', fontWeight:700 },
  navIcon:     { fontSize:'15px', width:'20px', textAlign:'center', flexShrink:0 },
  navBadge:    { background:'var(--red)', color:'#fff', fontSize:'10px', fontWeight:700, padding:'1px 6px', borderRadius:'99px', minWidth:'18px', textAlign:'center' },
  activeBar:   { position:'absolute', left:0, top:0, bottom:0, width:'3px', background:'var(--red)' },
  signOutBtn:  { width:'100%', background:'none', border:'1px solid rgba(255,255,255,.2)', color:'rgba(255,255,255,.5)', padding:'9px', fontFamily:'var(--font-body)', fontSize:'11px', fontWeight:700, letterSpacing:'1px', textTransform:'uppercase', cursor:'pointer' },

  /* Main */
  main:        { flex:1, padding:'36px', minWidth:0 },
  panel:       { animation:'fadeIn .25s ease' },
  spinner:     { width:'36px', height:'36px', border:'3px solid #E5E5E5', borderTopColor:'var(--red)', borderRadius:'50%', animation:'spin .8s linear infinite' },

  /* Typography */
  pageTitle:   { fontFamily:'var(--font-display)', fontSize:'clamp(22px,3vw,34px)', letterSpacing:'2px', color:'#111', margin:0, lineHeight:1.1 },
  pageSub:     { fontSize:'13px', color:'#9CA3AF', margin:'5px 0 0' },

  /* Cards */
  card:        { background:'#fff', border:'1px solid #E5E5E5', boxShadow:'0 1px 3px rgba(0,0,0,.04)', overflow:'auto' },
  cardHead:    { display:'flex', justifyContent:'space-between', alignItems:'center', padding:'16px 20px', borderBottom:'1px solid #F3F4F6' },
  cardTitle:   { fontFamily:'var(--font-display)', fontSize:'12px', letterSpacing:'2.5px', color:'#9CA3AF' },
  settingsCard:{ background:'#fff', border:'1px solid #E5E5E5', padding:'24px', boxShadow:'0 1px 3px rgba(0,0,0,.04)', marginBottom:0 },
  sectionTitle:{ fontFamily:'var(--font-display)', fontSize:'12px', letterSpacing:'2px', color:'#6B7280', marginBottom:'20px', paddingBottom:'12px', borderBottom:'1px solid #F3F4F6' },

  /* Stats */
  statGrid:    { display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:'14px', marginBottom:'20px' },
  statCard:    { background:'#fff', border:'1px solid #E5E5E5', padding:'18px', boxShadow:'0 1px 3px rgba(0,0,0,.04)' },
  statLabel:   { fontSize:'10px', fontWeight:700, letterSpacing:'1.5px', textTransform:'uppercase', color:'#9CA3AF', margin:'0 0 6px' },
  statVal:     { fontFamily:'var(--font-display)', fontSize:'28px', letterSpacing:'1px', lineHeight:1, margin:0 },

  /* Table */
  table:       { width:'100%', borderCollapse:'collapse', minWidth:'600px' },
  th:          { padding:'10px 16px', fontSize:'10px', fontWeight:700, letterSpacing:'1.5px', textTransform:'uppercase', color:'#9CA3AF', borderBottom:'1px solid #F3F4F6', textAlign:'left', background:'#FAFAFA', whiteSpace:'nowrap' },
  tr:          { borderBottom:'1px solid #F9FAFB' },
  td:          { padding:'13px 16px', fontSize:'13px', verticalAlign:'middle' },

  /* Buttons */
  btnRed:      { background:'var(--red)', color:'#fff', border:'none', padding:'10px 22px', fontFamily:'var(--font-body)', fontSize:'12px', fontWeight:700, letterSpacing:'.5px', cursor:'pointer', textDecoration:'none', display:'inline-block' },
  btnOutline:  { background:'#fff', color:'#374151', border:'1.5px solid #D1D5DB', padding:'9px 20px', fontFamily:'var(--font-body)', fontSize:'12px', fontWeight:700, cursor:'pointer' },
  actionBtn:   { background:'#fff', border:'1.5px solid #E5E5E5', color:'#374151', padding:'5px 12px', fontFamily:'var(--font-body)', fontSize:'11px', fontWeight:700, cursor:'pointer' },
  linkBtn:     { background:'none', border:'none', color:'var(--red)', fontWeight:700, fontSize:'12px', cursor:'pointer', fontFamily:'var(--font-body)' },
  filterTab:   { padding:'10px 18px', border:'none', background:'none', fontFamily:'var(--font-body)', fontSize:'12px', fontWeight:600, cursor:'pointer', color:'#6B7280', borderBottom:'2px solid transparent', marginBottom:'-2px' },
  filterTabActive: { color:'var(--red)', borderBottom:'2px solid var(--red)' },

  /* Wishlist */
  wishGrid:    { display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:'16px' },
  wishCard:    { background:'#fff', border:'1px solid #E5E5E5', position:'relative', boxShadow:'0 1px 3px rgba(0,0,0,.04)' },
  wishDel:     { position:'absolute', top:'8px', right:'8px', zIndex:2, background:'rgba(0,0,0,.5)', color:'#fff', border:'none', width:'24px', height:'24px', borderRadius:'50%', cursor:'pointer', fontSize:'14px', display:'flex', alignItems:'center', justifyContent:'center', lineHeight:1 },
  wishImg:     { height:'200px', background:'#F3F4F6', display:'flex', alignItems:'center', justifyContent:'center', overflow:'hidden' },
  wishCartBtn: { width:'100%', background:'#111', color:'#fff', border:'none', padding:'10px', fontFamily:'var(--font-body)', fontSize:'12px', fontWeight:700, cursor:'pointer', letterSpacing:'.5px' },

  /* Addresses */
  addrGrid:    { display:'grid', gridTemplateColumns:'repeat(2,1fr)', gap:'16px' },
  addrCard:    { background:'#fff', border:'2px solid #E5E5E5', padding:'20px', boxShadow:'0 1px 3px rgba(0,0,0,.04)' },
  addrAddCard: { background:'#fff', border:'2px dashed #E5E5E5', padding:'32px', display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', cursor:'pointer', transition:'border-color .2s', minHeight:'160px' },
  addrBtn:     { background:'#fff', border:'1.5px solid #E5E5E5', padding:'6px 14px', fontFamily:'var(--font-body)', fontSize:'11px', fontWeight:700, cursor:'pointer', color:'#374151' },

  /* Loyalty */
  loyaltyCard: { background:'linear-gradient(135deg,#111 0%,#2A1A00 100%)', padding:'28px 32px', marginBottom:'0', position:'relative', overflow:'hidden' },
  perksGrid:   { display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:'14px' },
  perkCard:    { background:'#fff', border:'1px solid #E5E5E5', padding:'20px', boxShadow:'0 1px 3px rgba(0,0,0,.04)' },

  /* Coupons */
  couponGrid:  { display:'grid', gridTemplateColumns:'repeat(2,1fr)', gap:'16px' },
  couponCard:  { border:'2px dashed', padding:'20px', boxShadow:'0 1px 3px rgba(0,0,0,.04)' },
  couponBtn:   { border:'none', padding:'9px 20px', fontFamily:'var(--font-body)', fontSize:'12px', fontWeight:700, cursor:'pointer', letterSpacing:'.5px' },

  /* Form */
  formGrid:    { display:'grid', gridTemplateColumns:'1fr 1fr', gap:'14px', marginBottom:'20px' },
  field:       { display:'flex', flexDirection:'column', gap:'5px' },
  label:       { fontSize:'10px', fontWeight:700, letterSpacing:'1px', textTransform:'uppercase', color:'#6B7280' },
  input:       { border:'1.5px solid #E5E5E5', background:'#FAFAFA', padding:'10px 12px', fontFamily:'var(--font-body)', fontSize:'13px', outline:'none', width:'100%', boxSizing:'border-box' },

  /* Notifications */
  notifRow:    { display:'flex', justifyContent:'space-between', alignItems:'center', padding:'16px 0', gap:'20px' },
  toggle:      { width:'40px', height:'22px', minWidth:'40px', borderRadius:'99px', border:'none', cursor:'pointer', position:'relative', transition:'background .2s', flexShrink:0 },
  knob:        { position:'absolute', top:'1px', width:'20px', height:'20px', borderRadius:'50%', background:'#fff', boxShadow:'0 1px 3px rgba(0,0,0,.3)', transition:'transform .2s' },

  /* Modal */
  overlay:     { position:'fixed', inset:0, background:'rgba(0,0,0,.5)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:9999, padding:'20px' },
  modal:       { background:'#fff', width:'560px', maxWidth:'100%', maxHeight:'90vh', overflow:'auto', padding:'28px' },
  modalTitle:  { fontFamily:'var(--font-display)', fontSize:'16px', letterSpacing:'2px', color:'#111' },
  closeBtn:    { background:'none', border:'none', fontSize:'18px', cursor:'pointer', color:'#666', padding:'4px 8px', lineHeight:1 },

  /* Misc */
  emptyMsg:    { padding:'24px 20px', fontSize:'14px', color:'#9CA3AF', margin:0 },
  emptyBox:    { background:'#fff', border:'1px solid #E5E5E5', padding:'60px 20px', textAlign:'center', fontSize:'14px', color:'#6B7280', display:'flex', flexDirection:'column', alignItems:'center', gap:'8px' },
};
